import { useCallback, useEffect, useMemo, useState, type FormEvent } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import Footer from "@/components/feature/Footer";
import Navbar from "@/components/feature/Navbar";
import { WHATSAPP_COMMUNITY_URL } from "@/constants/social";
import {
  addCommunityForumComment,
  createCommunitySquad,
  createCommunityForumPost,
  fetchCommunitySquads,
  fetchCommunityTeams,
  fetchCommunityForumPosts,
  fetchPrivateDiscussionThreads,
  joinCommunitySquad,
  requestTeamJoin,
  toggleCommunityForumLike,
  uploadCommunityImage,
  type PrivateDiscussionThread,
  type CreateSquadInput,
  type PublicForumPostItem,
  type PublicSquadItem,
  type PublicTeamItem
} from "@/features/public/api";
import {
  hasMemberAccessToken,
  readStoredMemberUser,
  restoreMemberAccessToken,
  subscribeToMemberSessionChanges,
  withMemberSession as withStoredMemberSession
} from "@/features/public/memberSession";
import { forumCategories } from "@/mocks/forum";
import { connectCommunityRealtime } from "@/shared/realtime/communityRealtime";

import ForumComposerModal, { type ForumComposerPayload } from "./components/ForumComposerModal";
import ForumPostCard from "./components/ForumPostCard";

const SPORT_OPTIONS = [
  "Football",
  "Basketball",
  "Volleyball",
  "Tennis de Table",
  "Natation",
  "Athletisme",
  "Randonnée",
  "Gaming"
];

const EMPTY_SQUAD_FORM: CreateSquadInput = {
  name: "",
  sport: "Football",
  description: ""
};

const SQUAD_ACCENTS: Record<string, { solid: string }> = {
  "bg-orange-500": { solid: "#f97316" },
  "bg-teal-600": { solid: "#0d9488" },
  "bg-purple-500": { solid: "#a855f7" },
  "bg-green-600": { solid: "#16a34a" },
  "bg-blue-500": { solid: "#3b82f6" },
  "bg-cyan-500": { solid: "#06b6d4" },
  "bg-amber-500": { solid: "#f59e0b" },
  "bg-pink-500": { solid: "#ec4899" },
  "bg-yellow-500": { solid: "#eab308" },
  "bg-gray-700": { solid: "#374151" }
};

function getSquadAccent(color: string) {
  return SQUAD_ACCENTS[color] ?? SQUAD_ACCENTS["bg-gray-700"];
}

function resolveCommunityAccent(color: string) {
  if (color.startsWith("#")) {
    return color;
  }

  return getSquadAccent(color).solid;
}

function PlaceholderAvatar() {
  return (
    <div className="w-8 h-8 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-gray-400">
      <i className="ri-user-line text-sm"></i>
    </div>
  );
}

function TeamPlaceholderBadge({ label }: { label: string }) {
  return (
    <div className="w-9 h-9 rounded-full border-2 border-white bg-gray-100 flex items-center justify-center text-gray-500 text-xs font-semibold">
      {label.charAt(0).toUpperCase()}
    </div>
  );
}

function SquadCard({
  squad,
  currentUserId,
  isLoggedIn,
  entering,
  joining,
  onJoin,
  onEnter,
  onRequireLogin
}: {
  squad: PublicSquadItem;
  currentUserId: string | null;
  isLoggedIn: boolean;
  entering: boolean;
  joining: boolean;
  onJoin: (squadId: string) => void;
  onEnter: (squadId: string) => void;
  onRequireLogin: () => void;
}) {
  const isFull = squad.members.length >= squad.maxMembers || squad.status === "FULL";
  const isMember = Boolean(currentUserId && squad.members.some((member) => member.id === currentUserId));
  const visibleMembers = [...squad.members];
  const accent = getSquadAccent(squad.color);

  while (visibleMembers.length < 4) {
    visibleMembers.push({
      id: `placeholder-${visibleMembers.length}`,
      name: "Membre à venir",
      avatar: null,
      online: false
    });
  }

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 group">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: accent.solid }}
        >
          <i className={`${squad.icon} text-white text-2xl`}></i>
        </div>
        <div className="text-right">
          <span className="inline-flex text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {squad.category}
          </span>
          <div className="mt-2 text-xs text-gray-400">
            {squad.members.length}/{squad.maxMembers} membres
          </div>
        </div>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
        {squad.name}
      </h3>
      <p className="text-sm text-gray-600 mb-5 min-h-[44px]">{squad.description}</p>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {visibleMembers.slice(0, 4).map((member, index) =>
              member.avatar ? (
                <img
                  key={`${member.id}-${index}`}
                  src={member.avatar}
                  alt={member.name}
                  className="w-8 h-8 rounded-full border-2 border-white object-cover"
                />
              ) : (
                <PlaceholderAvatar key={`${member.id}-${index}`} />
              )
            )}
          </div>
        </div>

        <button
          onClick={() => {
            if (!isLoggedIn) {
              onRequireLogin();
              return;
            }

            if (isMember) {
              onEnter(squad.id);
              return;
            }

            if (!isFull) {
              onJoin(squad.id);
            }
          }}
          disabled={entering || joining || (!isMember && isFull)}
          className={`text-sm font-semibold flex items-center gap-1 transition-colors ${
            entering || (!isMember && isFull)
              ? "text-gray-400 cursor-not-allowed"
              : "text-orange-600 hover:text-orange-700"
          }`}
        >
          {entering ? "Ouverture..." : joining ? "Connexion..." : isMember ? "Entrer" : isFull ? "Complet" : "Rejoindre"}
          {!entering && (isMember || !isFull) ? <i className="ri-arrow-right-line"></i> : null}
        </button>
      </div>
    </div>
  );
}

function TeamCard({
  team,
  currentUserId,
  isLoggedIn,
  entering,
  requesting,
  onEnter,
  onRequestJoin,
  onRequireLogin
}: {
  team: PublicTeamItem;
  currentUserId: string | null;
  isLoggedIn: boolean;
  entering: boolean;
  requesting: boolean;
  onEnter: (teamId: string) => void;
  onRequestJoin: (teamId: string) => void;
  onRequireLogin: () => void;
}) {
  const accent = resolveCommunityAccent(team.couleur);
  const isMember = Boolean(team.isMember || (currentUserId && team.joueurs.some((joueur) => joueur.userId === currentUserId)));
  const requestStatus = team.joinRequestStatus ?? null;
  const canRequestJoin = !isMember && (requestStatus === null || requestStatus === "REFUSED");
  const actionLabel = entering
    ? "Ouverture..."
    : requesting
      ? "Envoi..."
      : isMember
        ? "Entrer"
        : requestStatus === "PENDING"
          ? "Demande envoyée"
          : requestStatus === "REFUSED"
            ? "Redemander"
            : "Demander à intégrer";

  return (
    <div className="bg-white rounded-xl p-6 shadow-sm hover:shadow-lg transition-all duration-300 border border-gray-100 group">
      <div className="flex items-start justify-between mb-4 gap-3">
        <div
          className="w-14 h-14 rounded-xl flex items-center justify-center shadow-sm"
          style={{ backgroundColor: accent }}
        >
          <i className="ri-shield-star-line text-white text-2xl"></i>
        </div>
        <div className="text-right">
          <span className="inline-flex text-sm font-medium text-gray-600 bg-gray-100 px-3 py-1 rounded-full">
            {team.sport}
          </span>
          <div className="mt-2 text-xs text-gray-400">
            {team.joueurs.length} joueur{team.joueurs.length > 1 ? "s" : ""}
          </div>
        </div>
      </div>

      <h3 className="text-lg font-bold text-gray-900 mb-2 group-hover:text-orange-600 transition-colors">
        {team.nom}
      </h3>
      <p className="text-sm text-gray-600 mb-1">
        Entraîneur: <span className="font-medium text-gray-800">{team.entraineur || "Non renseigné"}</span>
      </p>
      <p className="text-sm text-gray-500 mb-5 min-h-[44px]">
        {isMember
          ? "Vous faites partie de cette équipe et pouvez entrer dans son espace privé."
          : requestStatus === "PENDING"
            ? "Votre demande est en attente de validation par l'administration."
            : requestStatus === "REFUSED"
              ? "Votre dernière demande a été refusée. Vous pouvez envoyer une nouvelle demande si nécessaire."
              : "Demandez à intégrer cette équipe. L'accès privé sera ouvert après validation admin."}
      </p>

      <div className="flex items-center justify-between gap-4">
        <div className="flex items-center gap-2">
          <div className="flex -space-x-2">
            {team.joueurs.slice(0, 4).map((joueur) => (
              <TeamPlaceholderBadge key={`${team.id}-${joueur.id}`} label={joueur.nom} />
            ))}
          </div>
        </div>

        <button
          onClick={() => {
            if (isMember) {
              onEnter(team.id);
              return;
            }

            if (!isLoggedIn) {
              onRequireLogin();
              return;
            }

            if (canRequestJoin) {
              onRequestJoin(team.id);
            }
          }}
          disabled={entering || requesting || (!isMember && requestStatus === "PENDING")}
          className={`text-sm font-semibold flex items-center gap-1 transition-colors ${
            isMember || canRequestJoin
              ? "text-orange-600 hover:text-orange-700"
              : "text-gray-400 cursor-not-allowed"
          }`}
        >
          {actionLabel}
          {entering || requesting ? <i className="ri-loader-4-line animate-spin"></i> : isMember || canRequestJoin ? <i className="ri-arrow-right-line"></i> : null}
        </button>
      </div>
    </div>
  );
}

function PrivateGroupCard({
  thread,
  onEnter
}: {
  thread: PrivateDiscussionThread;
  onEnter: (chatId: string) => void;
}) {
  const accent = resolveCommunityAccent(thread.color);
  const badgeClass = thread.scope === "TEAM" ? "bg-teal-50 text-[#1A8C7A]" : "bg-orange-50 text-[#E8521A]";
  const badgeLabel = thread.scope === "TEAM" ? "Mon équipe" : "Mon squad";

  return (
    <div className="bg-white rounded-2xl border border-gray-100 p-5 hover:border-orange-200 transition-colors">
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-3 min-w-0">
          <div
            className="w-12 h-12 rounded-xl flex items-center justify-center shadow-sm flex-shrink-0"
            style={{ backgroundColor: accent }}
          >
            <i className={`${thread.icon} text-white text-xl`}></i>
          </div>
          <div className="min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <h3 className="font-bold text-[#1A1A1A] truncate">{thread.name}</h3>
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium ${badgeClass}`}>
                {badgeLabel}
              </span>
            </div>
            <p className="text-sm text-gray-500 mt-1">{thread.sport}</p>
            <p className="text-sm text-gray-400 mt-2 line-clamp-2">
              {thread.lastMessage || "Aucun message pour le moment. Entrez dans cet espace pour démarrer les échanges."}
            </p>
          </div>
        </div>

        <button
          onClick={() => onEnter(thread.id)}
          className="inline-flex items-center gap-2 px-4 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] transition-colors whitespace-nowrap"
        >
          {thread.unread > 0 ? (
            <span className="inline-flex min-w-5 items-center justify-center rounded-full bg-white/20 px-1.5 py-0.5 text-[10px] font-bold text-white">
              {thread.unread > 99 ? "99+" : thread.unread}
            </span>
          ) : null}
          Entrer
          <i className="ri-arrow-right-line"></i>
        </button>
      </div>

      <div className="mt-4 flex items-center justify-between gap-3 flex-wrap">
        <div className="flex -space-x-2">
          {thread.members.slice(0, 4).map((member) =>
            member.avatar ? (
              <img
                key={`${thread.id}-${member.id ?? member.name}`}
                src={member.avatar}
                alt={member.name}
                className="w-9 h-9 rounded-full border-2 border-white object-cover"
              />
            ) : (
              <PlaceholderAvatar key={`${thread.id}-${member.id ?? member.name}`} />
            )
          )}
        </div>
        <div className="text-xs text-gray-400">
          {thread.members.length} membre{thread.members.length > 1 ? "s" : ""}
        </div>
      </div>
    </div>
  );
}

export default function CommunityPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [activeTab, setActiveTab] = useState<"squads" | "forum">("squads");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUserId, setCurrentUserId] = useState<string | null>(null);
  const [teams, setTeams] = useState<PublicTeamItem[]>([]);
  const [loadingTeams, setLoadingTeams] = useState(true);
  const [teamError, setTeamError] = useState<string | null>(null);
  const [squads, setSquads] = useState<PublicSquadItem[]>([]);
  const [loadingSquads, setLoadingSquads] = useState(true);
  const [squadError, setSquadError] = useState<string | null>(null);
  const [myTeamThreads, setMyTeamThreads] = useState<PrivateDiscussionThread[]>([]);
  const [loadingMyTeams, setLoadingMyTeams] = useState(false);
  const [mySquadThreads, setMySquadThreads] = useState<PrivateDiscussionThread[]>([]);
  const [loadingMySquads, setLoadingMySquads] = useState(false);
  const [enteringGroupKey, setEnteringGroupKey] = useState<string | null>(null);
  const [feedbackMessage, setFeedbackMessage] = useState<string | null>(null);
  const [feedbackError, setFeedbackError] = useState<string | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showForumComposer, setShowForumComposer] = useState(false);
  const [createForm, setCreateForm] = useState<CreateSquadInput>(EMPTY_SQUAD_FORM);
  const [creatingSquad, setCreatingSquad] = useState(false);
  const [joiningSquadId, setJoiningSquadId] = useState<string | null>(null);
  const [requestingTeamId, setRequestingTeamId] = useState<string | null>(null);
  const [forumPosts, setForumPosts] = useState<PublicForumPostItem[]>([]);
  const [loadingForum, setLoadingForum] = useState(true);
  const [forumError, setForumError] = useState<string | null>(null);
  const [creatingForumPost, setCreatingForumPost] = useState(false);
  const [likingPostId, setLikingPostId] = useState<string | null>(null);
  const [commentingPostId, setCommentingPostId] = useState<string | null>(null);

  const filteredPosts = useMemo(
    () => (selectedCategory === "all" ? forumPosts : forumPosts.filter((post) => post.category === selectedCategory)),
    [forumPosts, selectedCategory]
  );

  useEffect(() => {
    setActiveTab(location.pathname.includes("/forum") ? "forum" : "squads");
  }, [location.pathname]);

  const syncSessionState = useCallback(() => {
    const storedUser = readStoredMemberUser();
    setIsLoggedIn(hasMemberAccessToken());
    setCurrentUserId(storedUser?.id ?? null);
  }, []);

  useEffect(() => {
    syncSessionState();
    window.addEventListener("storage", syncSessionState);
    const unsubscribe = subscribeToMemberSessionChanges(syncSessionState);

    return () => {
      window.removeEventListener("storage", syncSessionState);
      unsubscribe();
    };
  }, [syncSessionState]);

  const loadSquads = useCallback(async () => {
    setLoadingSquads(true);
    setSquadError(null);

    try {
      const nextSquads = await fetchCommunitySquads();
      setSquads(nextSquads);
    } catch (caughtError) {
      setSquadError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les squads");
    } finally {
      setLoadingSquads(false);
    }
  }, []);

  useEffect(() => {
    void loadSquads();
  }, [loadSquads]);

  const loadTeams = useCallback(async () => {
    setLoadingTeams(true);
    setTeamError(null);

    try {
      const accessToken = await restoreMemberAccessToken().catch(() => null);
      const nextTeams = await fetchCommunityTeams(accessToken);
      setTeams(nextTeams);
    } catch (caughtError) {
      setTeamError(caughtError instanceof Error ? caughtError.message : "Impossible de charger les équipes");
    } finally {
      setLoadingTeams(false);
    }
  }, []);

  useEffect(() => {
    void loadTeams();
  }, [loadTeams]);

  const loadMyPrivateThreads = useCallback(async (): Promise<PrivateDiscussionThread[]> => {
    setLoadingMyTeams(true);
    setLoadingMySquads(true);

    try {
      const payload = await withStoredMemberSession((token) => fetchPrivateDiscussionThreads(token));
      syncSessionState();

      if (payload === null) {
        setMyTeamThreads([]);
        setMySquadThreads([]);
        return [];
      }

      setMyTeamThreads(payload.filter((thread) => thread.scope === "TEAM"));
      setMySquadThreads(payload.filter((thread) => thread.scope === "SQUAD"));
      return payload;
    } catch {
      setMyTeamThreads([]);
      setMySquadThreads([]);
      return [];
    } finally {
      setLoadingMyTeams(false);
      setLoadingMySquads(false);
    }
  }, [syncSessionState]);

  useEffect(() => {
    if (!isLoggedIn) {
      setMyTeamThreads([]);
      setLoadingMyTeams(false);
      setMySquadThreads([]);
      setLoadingMySquads(false);
      return;
    }

    void loadMyPrivateThreads();
  }, [isLoggedIn, loadMyPrivateThreads]);

  useEffect(() => {
    if (!isLoggedIn) {
      return;
    }

    let isCancelled = false;
    let socket: Awaited<ReturnType<typeof connectCommunityRealtime>> = null;

    void (async () => {
      socket = await connectCommunityRealtime({ requiresAuth: true });

      if (!socket || isCancelled) {
        socket?.disconnect();
        return;
      }

      socket.on("community:chat-thread-updated", (updatedThread: PrivateDiscussionThread) => {
        if (updatedThread.scope === "TEAM") {
          setMyTeamThreads((current) => {
            const remainingThreads = current.filter((thread) => thread.id !== updatedThread.id);
            return [updatedThread, ...remainingThreads];
          });
          return;
        }

        setMySquadThreads((current) => {
          const remainingThreads = current.filter((thread) => thread.id !== updatedThread.id);
          return [updatedThread, ...remainingThreads];
        });
      });
    })();

    return () => {
      isCancelled = true;
      socket?.disconnect();
    };
  }, [isLoggedIn]);

  useEffect(() => {
    const refreshCommunityData = () => {
      void loadTeams();
      void loadSquads();

      if (hasMemberAccessToken()) {
        void loadMyPrivateThreads();
      }
    };

    window.addEventListener("focus", refreshCommunityData);

    return () => window.removeEventListener("focus", refreshCommunityData);
  }, [loadMyPrivateThreads, loadSquads, loadTeams]);

  const loadForumPosts = useCallback(async () => {
    setLoadingForum(true);
    setForumError(null);

    try {
      const accessToken = await restoreMemberAccessToken();
      const posts = await fetchCommunityForumPosts({
        accessToken: accessToken ?? undefined
      });
      const storedUser = readStoredMemberUser();
      setForumPosts(posts);
      setIsLoggedIn(Boolean(accessToken));
      setCurrentUserId(storedUser?.id ?? null);
    } catch (caughtError) {
      setForumError(caughtError instanceof Error ? caughtError.message : "Impossible de charger le forum");
    } finally {
      setLoadingForum(false);
    }
  }, []);

  useEffect(() => {
    void loadForumPosts();
  }, [loadForumPosts]);

  useEffect(() => {
    let isCancelled = false;
    let socket: Awaited<ReturnType<typeof connectCommunityRealtime>> = null;

    void (async () => {
      socket = await connectCommunityRealtime();

      if (!socket || isCancelled) {
        socket?.disconnect();
        return;
      }

      socket.on("community:forum-post-updated", (updatedPost: PublicForumPostItem) => {
        setForumPosts((current) => {
          const hasPost = current.some((post) => post.id === updatedPost.id);

          if (!hasPost) {
            return [updatedPost, ...current];
          }

          return current.map((post) => (post.id === updatedPost.id ? updatedPost : post));
        });
      });
    })();

    return () => {
      isCancelled = true;
      socket?.disconnect();
    };
  }, []);

  const requireLogin = useCallback(() => {
    const redirectTarget = `${location.pathname}${location.search}`;
    navigate(`/formulaires?tab=login&redirect=${encodeURIComponent(redirectTarget)}`);
  }, [location.pathname, location.search, navigate]);

  const executeMemberAction = useCallback(
    async function executeMemberAction<T>(action: (accessToken: string) => Promise<T>) {
      const payload = await withStoredMemberSession(action);
      syncSessionState();

      if (payload === null) {
        setIsLoggedIn(false);
        requireLogin();
      }

      return payload;
    },
    [requireLogin, syncSessionState]
  );

  const handleCreateSquad = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    setFeedbackError(null);
    setFeedbackMessage(null);
    setCreatingSquad(true);

    try {
      const payload = await executeMemberAction((token) => createCommunitySquad(token, createForm));

      if (payload === null) {
        return;
      }

      setShowCreateModal(false);
      setCreateForm(EMPTY_SQUAD_FORM);
      await loadMyPrivateThreads();
      setFeedbackMessage("Votre squad a bien été créé. Vous pouvez maintenant entrer dedans depuis la section Mes squads privés.");
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible de créer le squad");
    } finally {
      setCreatingSquad(false);
    }
  };

  const handleJoinSquad = async (squadId: string) => {
    setFeedbackError(null);
    setFeedbackMessage(null);
    setJoiningSquadId(squadId);

    try {
      const updatedSquad = await executeMemberAction((token) => joinCommunitySquad(token, squadId));

      if (!updatedSquad) {
        return;
      }

      setSquads((current) => current.map((squad) => (squad.id === updatedSquad.id ? updatedSquad : squad)));
      setFeedbackMessage("Vous avez rejoint le squad avec succes.");
      await loadMyPrivateThreads();
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible de rejoindre ce squad");
    } finally {
      setJoiningSquadId(null);
    }
  };

  const handleRequestTeamJoin = async (teamId: string) => {
    setFeedbackError(null);
    setFeedbackMessage(null);
    setRequestingTeamId(teamId);

    try {
      const joinRequest = await executeMemberAction((token) => requestTeamJoin(token, teamId));

      if (!joinRequest) {
        return;
      }

      setTeams((current) =>
        current.map((team) => (team.id === teamId ? { ...team, joinRequestStatus: "PENDING" } : team))
      );
      setFeedbackMessage("Votre demande d'intégration a été envoyée. L'admin doit la valider avant l'accès à l'équipe.");
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible d'envoyer la demande d'intégration.");
    } finally {
      setRequestingTeamId(null);
    }
  };

  const handleEnterSquad = useCallback((squadId: string) => {
    void (async () => {
      setFeedbackError(null);
      setEnteringGroupKey(`squad:${squadId}`);

      const matchingThread =
        mySquadThreads.find((thread) => thread.entityId === squadId)
        ?? (await loadMyPrivateThreads()).find((thread) => thread.scope === "SQUAD" && thread.entityId === squadId);

      setEnteringGroupKey(null);

      if (!matchingThread) {
        setFeedbackError("Ce squad n'est pas encore accessible dans vos discussions privées. Actualisez la page ou reconnectez-vous.");
        return;
      }

      navigate(`/social?tab=chat&chatId=${matchingThread.id}`);
    })();
  }, [loadMyPrivateThreads, mySquadThreads, navigate]);

  const handleEnterTeam = useCallback((teamId: string) => {
    void (async () => {
      setFeedbackError(null);
      setEnteringGroupKey(`team:${teamId}`);

      const matchingThread =
        myTeamThreads.find((thread) => thread.entityId === teamId)
        ?? (await loadMyPrivateThreads()).find((thread) => thread.scope === "TEAM" && thread.entityId === teamId);

      setEnteringGroupKey(null);

      if (!matchingThread) {
        setFeedbackError("Cette équipe n'est pas encore accessible dans vos discussions privées. Actualisez la page ou reconnectez-vous.");
        return;
      }

      navigate(`/social?tab=chat&chatId=${matchingThread.id}`);
    })();
  }, [loadMyPrivateThreads, myTeamThreads, navigate]);

  const handleEnterPrivateThread = useCallback((chatId: string) => {
    navigate(`/social?tab=chat&chatId=${chatId}`);
  }, [navigate]);

  const replaceForumPost = useCallback((nextPost: PublicForumPostItem) => {
    setForumPosts((current) => current.map((post) => (post.id === nextPost.id ? nextPost : post)));
  }, []);

  const handleCreateForumPost = async (payload: ForumComposerPayload) => {
    setFeedbackError(null);
    setFeedbackMessage(null);
    setCreatingForumPost(true);

    try {
      const createdPost = await executeMemberAction(async (token) => {
        let imageUrl: string | undefined;

        if (payload.imageFile) {
          const uploaded = await uploadCommunityImage(token, payload.imageFile);
          imageUrl = uploaded.url;
        }

        return createCommunityForumPost(token, {
          category: payload.category,
          title: payload.title,
          content: payload.content,
          image: imageUrl
        });
      });

      if (!createdPost) {
        return;
      }

      setForumPosts((current) => [createdPost, ...current]);
      setShowForumComposer(false);
      setFeedbackMessage("Votre publication a bien été ajoutée au forum.");
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible de publier sur le forum");
    } finally {
      setCreatingForumPost(false);
    }
  };

  const handleToggleForumLike = async (postId: string) => {
    setFeedbackError(null);
    setLikingPostId(postId);

    try {
      const updatedPost = await executeMemberAction((token) => toggleCommunityForumLike(token, postId));

      if (updatedPost) {
        replaceForumPost(updatedPost);
      }
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible de mettre à jour le like");
    } finally {
      setLikingPostId(null);
    }
  };

  const handleAddForumComment = async (postId: string, payload: { content?: string }) => {
    setFeedbackError(null);
    setCommentingPostId(postId);

    try {
      const updatedPost = await executeMemberAction((token) =>
        addCommunityForumComment(token, postId, {
          content: payload.content
        })
      );

      if (updatedPost) {
        replaceForumPost(updatedPost);
      }
    } catch (caughtError) {
      setFeedbackError(caughtError instanceof Error ? caughtError.message : "Impossible d'ajouter ce commentaire");
    } finally {
      setCommentingPostId(null);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />

      <section className="relative py-16 pt-36 overflow-hidden">
        <div className="absolute inset-0">
          <img
            src="https://readdy.ai/api/search-image?query=diverse%20african%20colleagues%20team%20gathering%20office%20celebration%20community%20togetherness%20senegal%20professional%20warm%20atmosphere&width=1600&height=400&seq=comm1&orientation=landscape"
            alt="Communaute ASC"
            className="w-full h-full object-cover object-top"
          />
          <div className="absolute inset-0 bg-[#E8521A]/80"></div>
        </div>

        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative">
          <div className="text-center text-white">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Communauté ASC</h1>
            <p className="text-xl opacity-90 max-w-2xl mx-auto">
              Rejoignez des squads, participez aux discussions et connectez-vous avec les membres
            </p>
          </div>
        </div>
      </section>

      <div className="sticky top-20 z-40 bg-white border-b border-gray-200 shadow-sm">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex gap-8">
            <button
              onClick={() => navigate("/communaute/squads")}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === "squads"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <i className="ri-team-line mr-2"></i>
              Équipes & Squads
            </button>
            <button
              onClick={() => navigate("/communaute/forum")}
              className={`py-4 px-2 font-medium border-b-2 transition-colors ${
                activeTab === "forum"
                  ? "border-orange-500 text-orange-600"
                  : "border-transparent text-gray-600 hover:text-gray-900"
              }`}
            >
              <i className="ri-discuss-line mr-2"></i>
              Forum
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
        {feedbackMessage ? (
          <div className="mb-6 rounded-xl border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
            {feedbackMessage}
          </div>
        ) : null}

        {feedbackError ? (
          <div className="mb-6 rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
            {feedbackError}
          </div>
        ) : null}

        {activeTab === "squads" ? (
          <div className="space-y-8">
            <div className="bg-gradient-to-r from-orange-500 to-orange-600 rounded-2xl p-8 text-white">
              <div className="flex flex-col md:flex-row items-center justify-between gap-6">
                <div>
                  <h2 className="text-2xl font-bold mb-2">Créer votre propre squad</h2>
                  <p className="opacity-90 max-w-2xl">
                    Les membres connectés peuvent proposer un squad. Le créateur accède immédiatement à son espace
                    privé, puis l&apos;admin valide le squad pour le rendre visible aux autres membres.
                  </p>
                </div>
                <button
                  onClick={() => {
                    if (!isLoggedIn) {
                      requireLogin();
                      return;
                    }

                    setShowCreateModal(true);
                  }}
                  className="px-6 py-3 rounded-lg font-semibold whitespace-nowrap transition-colors bg-white text-orange-600 hover:bg-gray-100"
                >
                  <i className="ri-add-line mr-2"></i>
                  Créer un squad
                </button>
              </div>
            </div>

            <div>
              {isLoggedIn ? (
                <div className="mb-8">
                  <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Mes équipes privées</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Les équipes auxquelles vous êtes rattaché apparaissent ici avec accès direct à leur espace privé.
                      </p>
                    </div>
                    <button
                      onClick={() => void loadMyPrivateThreads()}
                      className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-orange-200 hover:text-orange-600 transition-colors"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      Actualiser
                    </button>
                  </div>

                  {loadingMyTeams ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="h-40 rounded-2xl bg-white border border-gray-100 animate-pulse"></div>
                      ))}
                    </div>
                  ) : myTeamThreads.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {myTeamThreads.map((thread) => (
                        <PrivateGroupCard key={thread.id} thread={thread} onEnter={handleEnterPrivateThread} />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-teal-200 bg-teal-50/60 px-6 py-8">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-white text-[#1A8C7A] flex items-center justify-center flex-shrink-0">
                          <i className="ri-shield-star-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Aucune équipe privée pour le moment</h3>
                          <p className="mt-2 text-sm text-gray-600">
                            Dès qu&apos;un admin valide votre demande d&apos;intégration à une équipe, son espace privé apparaîtra ici.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Équipes créées</h2>
                  <p className="text-sm text-gray-500 mt-1">Les équipes sont affichées séparément des squads pour une lecture plus claire.</p>
                </div>
                <button
                  onClick={() => void loadTeams()}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-orange-200 hover:text-orange-600 transition-colors"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Actualiser
                </button>
              </div>

              {teamError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {teamError}
                </div>
              ) : null}

              {loadingTeams ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 3 }).map((_, index) => (
                    <div key={index} className="h-64 rounded-xl bg-white border border-gray-100 animate-pulse"></div>
                  ))}
                </div>
              ) : teams.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {teams.map((team) => (
                    <TeamCard
                      key={team.id}
                      team={team}
                      currentUserId={currentUserId}
                      isLoggedIn={isLoggedIn}
                      entering={enteringGroupKey === `team:${team.id}`}
                      requesting={requestingTeamId === team.id}
                      onEnter={handleEnterTeam}
                      onRequestJoin={handleRequestTeamJoin}
                      onRequireLogin={requireLogin}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
                  <i className="ri-shield-star-line text-4xl text-gray-300"></i>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">Aucune équipe créée pour le moment</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Les équipes apparaîtront ici dès qu&apos;elles seront créées par l&apos;administration.
                  </p>
                </div>
              )}
            </div>

            <div>
              {isLoggedIn ? (
                <div className="mb-8">
                  <div className="flex items-center justify-between gap-3 mb-4 flex-wrap">
                    <div>
                      <h2 className="text-2xl font-bold text-gray-900">Mes squads privés</h2>
                      <p className="text-sm text-gray-500 mt-1">
                        Les squads que vous créez ou rejoignez apparaissent ici, même avant leur validation publique.
                      </p>
                    </div>
                    <button
                      onClick={() => void loadMyPrivateThreads()}
                      className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-orange-200 hover:text-orange-600 transition-colors"
                    >
                      <i className="ri-refresh-line mr-2"></i>
                      Actualiser
                    </button>
                  </div>

                  {loadingMySquads ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {Array.from({ length: 2 }).map((_, index) => (
                        <div key={index} className="h-40 rounded-2xl bg-white border border-gray-100 animate-pulse"></div>
                      ))}
                    </div>
                  ) : mySquadThreads.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                      {mySquadThreads.map((thread) => (
                        <PrivateGroupCard key={thread.id} thread={thread} onEnter={handleEnterPrivateThread} />
                      ))}
                    </div>
                  ) : (
                    <div className="rounded-2xl border border-dashed border-orange-200 bg-orange-50/60 px-6 py-8">
                      <div className="flex items-start gap-3">
                        <div className="w-12 h-12 rounded-2xl bg-white text-[#E8521A] flex items-center justify-center flex-shrink-0">
                          <i className="ri-chat-3-line text-2xl"></i>
                        </div>
                        <div>
                          <h3 className="text-lg font-semibold text-gray-900">Aucun squad privé pour le moment</h3>
                          <p className="mt-2 text-sm text-gray-600">
                            Dès que vous créez un squad ou que vous rejoignez un squad validé, son espace privé de discussion apparaîtra ici.
                          </p>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              ) : null}

              <div className="flex items-center justify-between gap-3 mb-6 flex-wrap">
                <div>
                  <h2 className="text-2xl font-bold text-gray-900">Squads créés</h2>
                  <p className="text-sm text-gray-500 mt-1">Les squads sont affichés séparément des équipes. Seuls les squads validés par l&apos;admin apparaissent ici.</p>
                </div>
                <button
                  onClick={() => void loadSquads()}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 bg-white text-sm font-medium text-gray-600 hover:border-orange-200 hover:text-orange-600 transition-colors"
                >
                  <i className="ri-refresh-line mr-2"></i>
                  Actualiser
                </button>
              </div>

              {squadError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {squadError}
                </div>
              ) : null}

              {loadingSquads ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {Array.from({ length: 6 }).map((_, index) => (
                    <div key={index} className="h-64 rounded-xl bg-white border border-gray-100 animate-pulse"></div>
                  ))}
                </div>
              ) : squads.length > 0 ? (
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                  {squads.map((squad) => (
                    <SquadCard
                      key={squad.id}
                      squad={squad}
                      currentUserId={currentUserId}
                      isLoggedIn={isLoggedIn}
                      entering={enteringGroupKey === `squad:${squad.id}`}
                      joining={joiningSquadId === squad.id}
                      onJoin={handleJoinSquad}
                      onEnter={handleEnterSquad}
                      onRequireLogin={requireLogin}
                    />
                  ))}
                </div>
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
                  <i className="ri-team-line text-4xl text-gray-300"></i>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">Aucun squad validé pour le moment</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Proposez un nouveau squad et l&apos;admin pourra le valider pour l&apos;ouvrir à la communauté.
                  </p>
                </div>
              )}
            </div>

            <div className="bg-teal-50 rounded-xl p-6 border border-teal-100">
              <h3 className="text-lg font-bold text-teal-900 mb-4 flex items-center gap-2">
                <i className="ri-information-line"></i>
                Règlement des squads
              </h3>
              <ul className="space-y-2 text-teal-800">
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-1"></i>
                  <span>Maximum 4 personnes par squad</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-1"></i>
                  <span>Chaque nouveau squad passe d&apos;abord par une validation admin</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-1"></i>
                  <span>Le créateur peut accéder tout de suite au chat privé de son squad</span>
                </li>
                <li className="flex items-start gap-2">
                  <i className="ri-check-line mt-1"></i>
                  <span>Une fois validé, le squad devient visible et rejoignable par les membres connectés</span>
                </li>
              </ul>
            </div>
          </div>
        ) : (
          <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
            <div className="lg:col-span-1 space-y-6">
              <button
                onClick={() => {
                  if (!isLoggedIn) {
                    requireLogin();
                    return;
                  }

                  setShowForumComposer(true);
                }}
                className="w-full bg-orange-500 text-white py-3 rounded-xl font-semibold hover:bg-orange-600 transition-colors"
              >
                <i className={`${isLoggedIn ? "ri-add-line" : "ri-login-box-line"} mr-2`}></i>
                {isLoggedIn ? "Nouvelle publication" : "Se connecter pour publier"}
              </button>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Catégories</h3>
                <div className="space-y-2">
                  <button
                    onClick={() => setSelectedCategory("all")}
                    className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors ${
                      selectedCategory === "all" ? "bg-orange-100 text-orange-700" : "hover:bg-gray-100"
                    }`}
                  >
                    Toutes les catégories
                  </button>
                  {forumCategories.map((category) => (
                    <button
                      key={category.id}
                      onClick={() => setSelectedCategory(category.id)}
                      className={`w-full text-left px-3 py-2 rounded-lg text-sm transition-colors flex items-center gap-2 ${
                        selectedCategory === category.id ? "bg-orange-100 text-orange-700" : "hover:bg-gray-100"
                      }`}
                    >
                      <span className={`w-2 h-2 rounded-full ${category.color}`}></span>
                      {category.name}
                    </button>
                  ))}
                </div>
              </div>

              <div className="bg-white rounded-xl p-4 shadow-sm border border-gray-100">
                <h3 className="font-semibold text-gray-900 mb-4">Suivez-nous</h3>
                <div className="flex gap-3">
                  <a
                    href={WHATSAPP_COMMUNITY_URL}
                    target="_blank"
                    rel="noreferrer"
                    className="w-10 h-10 rounded-full bg-green-500 flex items-center justify-center text-white hover:opacity-80 transition-opacity"
                  >
                    <i className="ri-whatsapp-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-blue-600 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <i className="ri-linkedin-fill text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-black flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <i className="ri-twitter-x-line text-lg"></i>
                  </a>
                  <a href="#" className="w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white hover:opacity-80 transition-opacity">
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                </div>
              </div>
            </div>

            <div className="lg:col-span-3 space-y-6">
              {!isLoggedIn ? (
                <div className="rounded-2xl border border-orange-100 bg-orange-50 px-5 py-4 text-sm text-orange-800">
                  Le forum reste visible publiquement, mais seules les personnes connectées peuvent publier, aimer ou commenter.
                </div>
              ) : null}

              {forumError ? (
                <div className="rounded-xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-600">
                  {forumError}
                </div>
              ) : null}

              {loadingForum ? (
                Array.from({ length: 3 }).map((_, index) => (
                  <div key={index} className="h-72 rounded-xl bg-white border border-gray-100 animate-pulse"></div>
                ))
              ) : filteredPosts.length > 0 ? (
                filteredPosts.map((post) => (
                  <ForumPostCard
                    key={post.id}
                    categories={forumCategories}
                    post={post}
                    isLoggedIn={isLoggedIn}
                    liking={likingPostId === post.id}
                    commenting={commentingPostId === post.id}
                    onRequireLogin={requireLogin}
                    onToggleLike={handleToggleForumLike}
                    onComment={handleAddForumComment}
                  />
                ))
              ) : (
                <div className="rounded-2xl border border-dashed border-gray-200 bg-white px-6 py-12 text-center">
                  <i className="ri-discuss-line text-4xl text-gray-300"></i>
                  <h3 className="mt-4 text-lg font-semibold text-gray-900">Aucune publication trouvée</h3>
                  <p className="mt-2 text-sm text-gray-500">
                    Commencez la discussion en publiant une annonce, une idée ou un retour d'expérience.
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {showCreateModal ? (
        <div className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4" onClick={() => setShowCreateModal(false)}>
          <div
            className="w-full max-w-xl rounded-[28px] bg-white border border-gray-100 shadow-[0_30px_100px_rgba(0,0,0,0.16)] overflow-hidden"
            onClick={(event) => event.stopPropagation()}
          >
            <div className="px-6 py-5 border-b border-gray-100 flex items-center justify-between">
              <div>
                <h3 className="text-lg font-bold text-[#1A1A1A]">Proposer un squad</h3>
                <p className="text-sm text-gray-500 mt-1">L&apos;admin devra valider ce squad avant publication.</p>
              </div>
              <button
                onClick={() => setShowCreateModal(false)}
                className="w-9 h-9 rounded-xl hover:bg-gray-100 transition-colors flex items-center justify-center text-gray-500"
              >
                <i className="ri-close-line text-lg"></i>
              </button>
            </div>

            <form onSubmit={handleCreateSquad} className="p-6 space-y-5">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Nom du squad</label>
                <input
                  type="text"
                  value={createForm.name}
                  onChange={(event) => setCreateForm((current) => ({ ...current, name: event.target.value }))}
                  placeholder="Ex: Lions du Plateau"
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors"
                  required
                  minLength={3}
                  maxLength={80}
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Discipline</label>
                <select
                  value={createForm.sport}
                  onChange={(event) => setCreateForm((current) => ({ ...current, sport: event.target.value }))}
                  className="w-full rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors bg-white"
                >
                  {SPORT_OPTIONS.map((sport) => (
                    <option key={sport} value={sport}>
                      {sport}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1.5">Description</label>
                <textarea
                  value={createForm.description}
                  onChange={(event) => setCreateForm((current) => ({ ...current, description: event.target.value }))}
                  placeholder="Expliquez l'objectif du squad, le rythme d'entrainement ou le profil recherché."
                  className="w-full min-h-[130px] rounded-xl border border-gray-200 px-4 py-3 text-sm focus:outline-none focus:border-[#E8521A] transition-colors resize-none"
                  required
                  minLength={10}
                  maxLength={240}
                />
                <div className="mt-2 text-right text-xs text-gray-400">{createForm.description.length}/240</div>
              </div>

              <div className="rounded-xl border border-orange-100 bg-orange-50 px-4 py-3 text-sm text-[#9A3E18]">
                Dès validation admin, votre squad apparaîtra dans la communauté et les membres connectés pourront le rejoindre.
              </div>

              <div className="flex items-center justify-end gap-3 pt-2">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2.5 rounded-xl border border-gray-200 text-sm font-medium text-gray-600 hover:bg-gray-50 transition-colors"
                >
                  Annuler
                </button>
                <button
                  type="submit"
                  disabled={creatingSquad}
                  className="px-5 py-2.5 rounded-xl bg-[#E8521A] text-white text-sm font-semibold hover:bg-[#d44a16] disabled:opacity-60 disabled:cursor-not-allowed transition-colors"
                >
                  {creatingSquad ? "Envoi..." : "Envoyer a l'admin"}
                </button>
              </div>
            </form>
          </div>
        </div>
      ) : null}

      {showForumComposer ? (
        <ForumComposerModal
          categories={forumCategories}
          submitting={creatingForumPost}
          onClose={() => setShowForumComposer(false)}
          onSubmit={(payload) => {
            void handleCreateForumPost(payload);
          }}
        />
      ) : null}

      <Footer />
    </div>
  );
}
